/**
 * Relationship Paths - Defines relationship paths between entity types
 *
 * Contains the complete mapping of valid relationship paths between different
 * entity types in the graph database, including direct relationships and
 * multi-hop paths through intermediate nodes.
 *
 * @module queryBuilderCore/relationships/relationshipPaths
 */

import { ACTIVE_DIRECTORY_RELATIONSHIP_LABELS } from '@/sections/insights/queryBuilder/legacy/entities/activeDirectory/ActiveDirectoryEntityConfig';
import { RelationshipPathConfig } from '@/sections/insights/queryBuilder/legacy/types/types';

/**
 * Relationship path configurations between entity types
 *
 * Keys use the format "SourceEntity->TargetEntity" to define valid paths.
 * Each path can be direct (single relationship) or indirect (through intermediate nodes).
 */
export const relationshipPaths: Record<string, RelationshipPathConfig[]> = {
  'Asset->Vulnerability': [
    {
      path: [
        {
          label: ['HAS_VULNERABILITY', 'HAS_ATTRIBUTE'],
          direction: 'forward',
          length: 2,
        },
      ],
    },
  ],
  'Vulnerability->Asset': [
    {
      path: [
        {
          label: ['HAS_VULNERABILITY', 'HAS_ATTRIBUTE'],
          direction: 'reverse',
          length: 2,
        },
      ],
    },
  ],

  'Asset->Technology': [
    {
      path: [
        {
          label: 'HAS_ATTRIBUTE',
          direction: 'forward',
          intermediate: 'Attribute',
        },
        { label: 'HAS_TECHNOLOGY', direction: 'forward' },
      ],
    },
  ],
  'Technology->Asset': [
    {
      path: [
        {
          label: 'HAS_TECHNOLOGY',
          direction: 'reverse',
          intermediate: 'Attribute',
        },
        { label: 'HAS_ATTRIBUTE', direction: 'reverse' },
      ],
    },
  ],

  'Asset->Seed': [
    {
      path: [
        {
          label: 'DISCOVERED',
          direction: 'forward',
          length: -1,
        },
      ],
    },
  ],
  'Seed->Asset': [
    {
      path: [
        {
          label: 'DISCOVERED',
          direction: 'reverse',
          length: -1,
        },
      ],
    },
  ],

  'Technology->Vulnerability': [
    { path: [{ label: 'HAS_VULNERABILITY', direction: 'forward' }] },
  ],
  'Vulnerability->Technology': [
    { path: [{ label: 'HAS_VULNERABILITY', direction: 'reverse' }] },
  ],

  'Technology->Seed': [
    { path: [{ label: 'DISCOVERED_BY', direction: 'forward' }] },

    {
      path: [
        {
          label: 'HAS_TECHNOLOGY',
          direction: 'reverse',
          intermediate: 'Asset',
        },
        { label: 'DISCOVERED', direction: 'reverse' },
      ],
    },
  ],
  'Seed->Technology': [
    { path: [{ label: 'DISCOVERED_BY', direction: 'reverse' }] },

    {
      path: [
        {
          label: 'DISCOVERED',
          direction: 'forward',
          intermediate: 'Asset',
        },
        { label: 'HAS_TECHNOLOGY', direction: 'forward' },
      ],
    },
  ],

  'Seed->Vulnerability': [
    { path: [{ label: 'HAS_RISK', direction: 'forward' }] },

    {
      path: [
        {
          label: 'DISCOVERED',
          direction: 'forward',
          intermediate: 'Asset',
        },
        { label: 'HAS_VULNERABILITY', direction: 'forward' },
      ],
    },
  ],
  'Vulnerability->Seed': [
    { path: [{ label: 'HAS_RISK', direction: 'reverse' }] },

    {
      path: [
        {
          label: 'HAS_VULNERABILITY',
          direction: 'reverse',
          intermediate: 'Asset',
        },
        { label: 'DISCOVERED', direction: 'reverse' },
      ],
    },
  ],

  'ActiveDirectory->Asset': [
    {
      path: [
        {
          label: 'HAS_ATTRIBUTE',
          direction: 'forward',
        },
      ],
    },
  ],
  'Asset->ActiveDirectory': [
    {
      path: [
        {
          label: 'HAS_ATTRIBUTE',
          direction: 'reverse',
        },
      ],
    },
  ],

  'ActiveDirectory->Vulnerability': [
    {
      path: [
        {
          label: ['HAS_VULNERABILITY', 'HAS_ATTRIBUTE'],
          direction: 'forward',
          length: 2,
        },
      ],
    },
  ],
  'Vulnerability->ActiveDirectory': [
    {
      path: [
        {
          label: ['HAS_VULNERABILITY', 'HAS_ATTRIBUTE'],
          direction: 'reverse',
          length: 2,
        },
      ],
    },
  ],

  'ActiveDirectory->Technology': [
    {
      path: [
        {
          label: 'HAS_ATTRIBUTE',
          direction: 'forward',
          intermediate: 'Attribute',
        },
        { label: 'HAS_TECHNOLOGY', direction: 'forward' },
      ],
    },
  ],
  'Technology->ActiveDirectory': [
    {
      path: [
        {
          label: 'HAS_TECHNOLOGY',
          direction: 'reverse',
          intermediate: 'Attribute',
        },
        { label: 'HAS_ATTRIBUTE', direction: 'reverse' },
      ],
    },
  ],

  'ActiveDirectory->Seed': [
    { path: [{ label: 'DISCOVERED', direction: 'reverse' }] },
  ],
  'Seed->ActiveDirectory': [
    { path: [{ label: 'DISCOVERED', direction: 'forward' }] },
  ],

  'ActiveDirectory->ActiveDirectory': [
    {
      path: [
        {
          label: Array.from(ACTIVE_DIRECTORY_RELATIONSHIP_LABELS),
          direction: 'forward',
        },
      ],
    },
  ],
};
